import React from "react"
import PropTypes from "prop-types"
import { DEFAULTS } from "@constants"
import { GLOBALS } from "@src/config"
import humanFileSize from "@src/helpers/humanFileSize"

/**
 * Polls the copy progress of files being imported (duplicated) into a file request,
 * cinerequest, or project, and renders a progress bar per file. Only shows copies started
 * after this component mounted, so previously imported files are excluded.
 */
class CopyProgress extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      files: [],
      loaded: false
    }
    // Small buffer so replicas created while the import request is in flight are included.
    this.since = new Date(Date.now() - 10000).toISOString()
  }
  componentDidMount() {
    this.poll()
    this.interval = setInterval(() => this.poll(), 4000)
  }
  componentWillUnmount() {
    clearInterval(this.interval)
    clearTimeout(this.completeTimeout)
  }
  isDone(files) {
    return files.length > 0 && files.every(file => file.copy_status === "complete" || file.copy_status === "failed")
  }
  poll() {
    const params = { ...this.props.params, since: this.since }
    const query = Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join("&")
    fetch(`${GLOBALS.API_URL}/files/copy-progress?${query}`, DEFAULTS)
      .then(res => res.json())
      .then(res => {
        const files = res.files || []
        this.setState({ files, loaded: true })
        if (this.isDone(files)) {
          clearInterval(this.interval)
          if (this.props.onComplete && !this.completeTimeout) {
            // Leave the "Import complete!" state visible for a beat before closing.
            this.completeTimeout = setTimeout(() => this.props.onComplete(), 1500)
          }
        }
      })
      .catch(() => {})
  }
  renderStatus(file) {
    if (file.copy_status === "complete") {
      return "Complete"
    }
    if (file.copy_status === "failed") {
      return "Failed"
    }
    return `${humanFileSize(file.copied_bytes) || "0 B"} of ${humanFileSize(file.copy_total_bytes) ||
      "--"} (${file.copy_progress || 0}%)`
  }
  render() {
    const { files, loaded } = this.state
    const done = loaded && this.isDone(files)
    return (
      <div className='py2'>
        {done ? (
          <p>
            <strong>Import complete!</strong>
          </p>
        ) : (
          <div className='flex items-center mb2'>
            <div className='spinner mr2'/>
            <p className='mb0'>
              <strong>Your file{files.length === 1 ? " is" : "s are"} being imported.</strong>
              <br/>
              You can safely leave this page — the import will continue in the background.
            </p>
          </div>
        )}
        {files.map(file => (
          <div key={file._id} className='mb2'>
            <div className='flex items-center justify-between'>
              <span className='truncate pr2'>{file.name}</span>
              <small className='nowrap'>{this.renderStatus(file)}</small>
            </div>
            <progress
              max={file.copy_total_bytes || 1}
              value={file.copy_status === "complete" ? file.copy_total_bytes || 1 : file.copied_bytes || 0}
              className='cs-progress col-12 block'/>
          </div>
        ))}
        {loaded && !files.length && <p>Preparing import…</p>}
      </div>
    )
  }
}

CopyProgress.propTypes = {
  params: PropTypes.object.isRequired,
  onComplete: PropTypes.func
}

export default CopyProgress
